import type { DeviceDetectionResult } from './types';
import { DeviceType } from './types';

/**
 * Device detection and routing helpers for invitation flow
 */

const MOBILE_USER_AGENT_REGEX =
  /android|webos|iphone|ipod|blackberry|iemobile|opera mini/i;
const TABLET_USER_AGENT_REGEX = /ipad|tablet|kindle|silk|playbook/i;

/**
 * Detects if the current device is a mobile device
 */
export const detectMobileDevice = (): boolean => {
  const userAgent =
    navigator.userAgent ??
    navigator.vendor ??
    (window as unknown as { opera?: string }).opera ??
    '';

  return MOBILE_USER_AGENT_REGEX.test(userAgent);
};

/**
 * Detects if the current device is a tablet
 */
export const detectTablet = (): boolean => {
  const userAgent = navigator.userAgent ?? navigator.vendor ?? '';

  return TABLET_USER_AGENT_REGEX.test(userAgent);
};

/**
 * Comprehensive device detection that returns device type and flags
 */
export const detectDevice = (): DeviceDetectionResult => {
  const isMobile = detectMobileDevice();
  const isTablet = detectTablet();

  let deviceType: DeviceType;

  if (isTablet) {
    deviceType = DeviceType.TABLET;
  } else if (isMobile) {
    deviceType = DeviceType.MOBILE;
  } else {
    deviceType = DeviceType.DESKTOP;
  }

  return {
    isMobile,
    isTablet,
    deviceType,
  };
};

/**
 * Creates a deep link URL for the mobile app token-entry page
 */
export const createDeepLinkUrl = (pairingCode: string): string => {
  return `${getDeepLinkScheme()}://token-entry?pairingCode=${encodeURIComponent(pairingCode)}`;
};

/**
 * Returns the desktop/tablet fallback URL for the invitation flow.
 *
 * The legacy `/idp/welcome` pairing-code page was removed when mobile
 * onboarding moved to user-portal-api (magic-token flow). We don't have a
 * desktop replacement yet (Flow B), so any desktop user that lands on
 * `/idp/invitation/:pairingCode` is redirected to the auth-error page,
 * which already directs them to the User Portal for enrollment.
 *
 * @param _pairingCode - retained for call-site stability; intentionally unused.
 */
export const createWebFlowUrl = (_pairingCode: string): string => {
  return '/idp/auth-error';
};

/**
 * Attempts to open the mobile app via deep link
 * Returns a Promise that resolves with whether the app was opened
 */
export const attemptAppLaunch = (deepLinkUrl: string): Promise<boolean> => {
  return new Promise(resolve => {
    let appOpened = false;

    const handleVisibilityChange = () => {
      if (document.hidden) {
        appOpened = true;
        // Page became hidden - app likely opened
      }
    };

    const handlePageFocus = () => {
      // Page regained focus
    };

    const handlePageBlur = () => {
      // Page lost focus
    };

    // Add event listeners
    document.addEventListener('visibilitychange', handleVisibilityChange);
    window.addEventListener('focus', handlePageFocus);
    window.addEventListener('blur', handlePageBlur);

    // Cleanup function
    const cleanup = () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      window.removeEventListener('focus', handlePageFocus);
      window.removeEventListener('blur', handlePageBlur);
    };

    try {
      window.location.href = deepLinkUrl;
    } catch (_error) {
      // Failed to launch app
      cleanup();
      resolve(false);

      return;
    }

    // Give the app time to open, then resolve
    setTimeout(() => {
      cleanup();
      resolve(appOpened);
    }, 2000);
  });
};

/**
 * Logs pairing code safely (masked for security)
 */
export const logPairingCodeSafely = (
  pairingCode: string,
  action: string
): void => {
  // Log action with masked pairing code for security
  console.info(`${action}:`, pairingCode.substring(0, 4) + '****');
};

function getDeepLinkScheme(): string {
  // Read from runtime environment config injected via Docker entrypoint
  const runtimeScheme = window.__ENV__?.DEEP_LINK_SCHEME;

  // Validate that the placeholder was actually replaced by the entrypoint script
  // If sed replacement failed, runtimeScheme would contain the literal placeholder
  const isValidScheme =
    runtimeScheme &&
    runtimeScheme !== '__DEEP_LINK_SCHEME__' &&
    runtimeScheme.length > 0;

  console.info(
    'using mobile schema: ',
    isValidScheme ? runtimeScheme : 'newcoreauth-dev (fallback)'
  );

  if (isValidScheme) {
    return runtimeScheme;
  }

  // Dev fallback (also used if placeholder wasn't replaced)
  return 'newcoreauth-dev';
}
